// frontend/src/utils/monthUtils.js
//
// Arithmetic and range helpers for 'YYYY-MM' month strings.
//
// Why strings and not Date: Date math around month boundaries is timezone-
// dependent — a Date constructed for "day 1 of month X" in local time lives in
// the previous UTC month for positive-offset timezones and flips labels in
// UTC-based formatters. Month strings sidestep that entirely.
//
// All helpers validate their input and return `null` (or `[]` / the caller's
// floor) for malformed values rather than silently producing NaN-poisoned
// output.
//
// Contract for every input named `ym`, `from`, `to`, `floor`:
//   - Expected format: 'YYYY-MM' (e.g. '2026-04'). No other shape is accepted.
//   - null / undefined / '' are treated as absent.

const YM_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

/**
 * Parse a 'YYYY-MM' string into { year, month } (month is 1-based).
 * Returns null for anything else.
 */
function parseYM(ym) {
  if (typeof ym !== 'string') return null;
  const m = YM_RE.exec(ym);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]) };
}

/**
 * Format { year, month1based } back to 'YYYY-MM'.
 */
function formatYM(year, month) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Inclusive list of 'YYYY-MM' strings between `from` and `to`.
 * Returns [] if either end is absent or malformed, or if `from > to`.
 */
export function monthsBetween(from, to) {
  const a = parseYM(from);
  const b = parseYM(to);
  if (!a || !b) return [];
  if (from > to) return [];
  const out = [];
  let y = a.year;
  let m = a.month;
  while (y < b.year || (y === b.year && m <= b.month)) {
    out.push(formatYM(y, m));
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

/**
 * Subtract N months from `ym`, clamped to `floor` (also 'YYYY-MM' or null).
 * Returns `floor` (or null) when `ym` is malformed, or when the result would
 * fall before `floor`.
 */
export function subMonths(ym, n, floor = null) {
  const parsed = parseYM(ym);
  if (!parsed || !Number.isFinite(n)) return floor || null;
  const total = parsed.year * 12 + (parsed.month - 1) - n;
  if (total < 0) return floor || null;
  const result = formatYM(Math.floor(total / 12), (total % 12) + 1);
  if (floor && result < floor) return floor;
  return result;
}

/**
 * Min of two 'YYYY-MM' strings. Treats null/undefined/malformed as absent —
 * returns the other argument (or null if both are absent).
 */
export function minMonth(a, b) {
  const va = parseYM(a) ? a : null;
  const vb = parseYM(b) ? b : null;
  if (!va) return vb;
  if (!vb) return va;
  return va < vb ? va : vb;
}

/**
 * Derive the "current month is partial" chip state from a todayIso ('YYYY-MM-DD',
 * always UTC from the bounds endpoint) and the user-selected toMonth ('YYYY-MM').
 *
 *   - Returns null when inputs are missing or when toMonth is strictly before
 *     the current month (no partial-data warning to show).
 *   - Otherwise returns { currentYm, day, totalDays } so the consumer can
 *     format the month label through its formatters hook and show
 *     "Day N of M in <Month>" in the chip.
 *
 * Arithmetic is done on string slices + `Date.UTC` so the result does not
 * depend on the browser's local timezone.
 */
export function partialMonthInfo(todayIso, toMonth) {
  if (typeof todayIso !== 'string' || typeof toMonth !== 'string') return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(todayIso)) return null;
  if (!parseYM(toMonth)) return null;
  const currentYm = todayIso.slice(0, 7);
  if (toMonth < currentYm) return null;
  const [y, m, d] = todayIso.split('-').map(Number);
  // Date.UTC with day=0 returns the last day of the previous month at UTC
  // midnight, so passing `m` here (1-based, pre-decrement) gives the last
  // day of month `m`. getUTCDate() is timezone-invariant.
  const totalDays = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { currentYm, day: d, totalDays };
}
